import React from 'react';
import { motion } from 'framer-motion';
import { Compass, KeyRound, Store, ChevronRight } from 'lucide-react';

/**
 * Fresh signup entry point (/select-role). Three direct choices — each
 * routes to its own onboarding flow with a durable `pendingPartnerIntent`
 * flag (see utils/partnerIntent.js) so Capacitor cold-starts can never
 * lose the user's choice mid-flow.
 *
 * Deep-link invites (guest/host/venue/staff) bypass this page entirely —
 * they land on their own entry page directly and stash a pendingInviteId
 * that always wins over partner intent (see VerifyEmailPage priority).
 */

const CARDS = [
  {
    id: 'guest',
    Icon: Compass,
    title: 'Guest Traveler',
    description: 'Unlock local venues and events during your stay.',
    tone: 'accent',
  },
  {
    id: 'host',
    Icon: KeyRound,
    title: 'Airbnb Host',
    description: 'Grow your earnings and delight every guest.',
    tone: 'accent',
  },
  {
    id: 'venue',
    Icon: Store,
    title: 'Food or Drink Venue',
    description: 'Reach travelers who explore beyond the tourist strip.',
    tone: 'accent',
  },
];

const cardStyle = {
  all: 'unset',
  cursor: 'pointer',
  width: '100%',
  maxWidth: 360,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '18px 18px',
  borderRadius: 22,
  background: 'var(--surface)',
  boxShadow: 'var(--premium-shadow-main)',
  border: '1px solid var(--card-border)',
  transition: 'transform 160ms ease, box-shadow 160ms ease',
};

export default function RoleSelectionPage({ onSelectRole }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="app-shell premium-page"
      data-testid="select-role-page"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 44px)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 40px)',
        paddingLeft: 20,
        paddingRight: 20,
      }}
    >
      <motion.img
        src="/allure-logo.png"
        alt="Allure"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{ width: 108, height: 96, objectFit: 'contain' }}
        data-testid="select-role-logo"
      />

      <motion.h1
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.18 }}
        style={{
          fontFamily: "'Amarante', serif",
          fontSize: 30,
          fontWeight: 400,
          color: 'var(--text)',
          marginTop: 14,
          marginBottom: 6,
          textAlign: 'center',
        }}
        data-testid="select-role-heading"
      >
        Welcome to Allure
      </motion.h1>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.28 }}
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 17,
          fontWeight: 400,
          color: 'var(--text-2)',
          margin: '0 0 26px',
          textAlign: 'center',
          maxWidth: 320,
          lineHeight: 1.4,
        }}
        data-testid="select-role-subtitle"
      >
        How would you like to join?
      </motion.p>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
        {CARDS.map((card, index) => (
          <motion.button
            key={card.id}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.35 + index * 0.08, ease: [0.25, 0.1, 0.25, 1] }}
            whileHover={{ scale: 1.015 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onSelectRole(card.id)}
            data-testid={`role-select-${card.id}`}
            style={cardStyle}
          >
            <div
              aria-hidden="true"
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: 'var(--accent-surface)',
                border: '1px solid rgba(196, 18, 48, 0.18)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <card.Icon size={24} color="var(--accent-purple)" strokeWidth={1.6} />
            </div>
            <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
              <p
                data-testid={`role-select-${card.id}-title`}
                style={{
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 19,
                  fontWeight: 700,
                  color: 'var(--text)',
                  margin: 0,
                  lineHeight: 1.15,
                }}
              >
                {card.title}
              </p>
              <p
                data-testid={`role-select-${card.id}-description`}
                style={{
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 14,
                  fontWeight: 400,
                  color: 'var(--text-2)',
                  margin: '4px 0 0',
                  lineHeight: 1.35,
                }}
              >
                {card.description}
              </p>
            </div>
            <ChevronRight size={20} color="var(--text-2)" strokeWidth={1.6} aria-hidden="true" />
          </motion.button>
        ))}
      </div>

      <div style={{ height: 22 }} />

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        style={{
          fontFamily: "'Inria Serif', serif",
          fontSize: 15,
          color: 'var(--text-2)',
          margin: '0',
          textAlign: 'center',
        }}
      >
        Already have an account?{' '}
        <button
          type="button"
          data-testid="select-role-signin-link"
          onClick={() => onSelectRole('signin')}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'var(--accent-purple)',
            fontFamily: 'inherit',
            fontSize: 'inherit',
            fontWeight: 700,
            cursor: 'pointer',
            textDecoration: 'underline',
            textUnderlineOffset: '3px',
          }}
        >
          Sign in
        </button>
      </motion.p>
    </motion.div>
  );
}
